import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, DefinitionList, DataPair, Banner } from '../patterns';
import {
  operatorSpraySessionService,
  MixPreview,
} from '../../services/operatorSpraySessionService';
import { formatMeasure } from '../../utils/formatMeasure';
import './MixAmountsModal.css';

export interface MixAmountsModalProps {
  comboId: string;
  show: boolean;
  onHide: () => void;
}

const round2 = (n: number): number => Number(n.toFixed(2));

/**
 * MixAmountsModal — per-tank mix table for the operator. Phase 6 /
 * Task 6.15.
 *
 * Fetches the live `MixPreview` payload (Task 6.6) from
 *   GET /api/v1/spray/operator-sessions/:id/mix-preview
 * on open, and renders:
 *   - a metadata block (machine + tank volume, intensity, total water,
 *     tank count)
 *   - a per-tank table (product, dose-per-tank)
 *   - a footnote pointing at the spray-instruction PDF
 *
 * Offline fallback (spec §3.3 step 2/3): if the network fetch fails the
 * modal swaps the table for an "offline — return to your manager" banner.
 * The full client-side mix computation from cached inputs is deferred —
 * Phase 6 ships the simple fallback.
 */
export const MixAmountsModal: React.FC<MixAmountsModalProps> = ({
  comboId,
  show,
  onHide,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState<MixPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    operatorSpraySessionService
      .getMixPreview(comboId)
      .then(d => {
        if (cancelled) return;
        setData(d);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        // Offline fallback per spec §3.3 step 2/3. The full client-side
        // mix computation from cached inputs is a deferred follow-up;
        // Phase 6 shows the "not available offline" message.
        setError(t('spray.operatorCombo.mixAmounts.offlineNotAvailable'));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, comboId, t]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      title={t('spray.operatorCombo.mixAmounts.title')}
      size="md"
    >
      <Modal.Body>
        {loading && (
          <div className="mix-amounts-modal__loading">{t('common.loading')}</div>
        )}
        {error && (
          <Banner
            tone="offline"
            title={error}
            testId="mix-amounts-modal-offline-banner"
          />
        )}
        {data && (
          <>
            <div className="mix-amounts-modal__meta">
              <DefinitionList columns={2}>
                <DataPair
                  label={t('spray.operatorCombo.mixAmounts.machine')}
                  value={t('spray.operatorCombo.mixAmounts.machineValue', {
                    name: data.machine.name,
                    tank: data.machine.tankVolume,
                  })}
                />
                <DataPair
                  label={t('spray.operatorCombo.mixAmounts.intensity')}
                  value={data.intensity}
                />
                <DataPair
                  label={t('spray.operatorCombo.mixAmounts.totalWater')}
                  value={t('spray.operatorCombo.mixAmounts.totalWaterValue', {
                    litres: round2(data.totalWaterL),
                  })}
                />
                <DataPair
                  label={t('spray.operatorCombo.mixAmounts.tankCount')}
                  value={String(data.tankCount)}
                />
              </DefinitionList>
            </div>
            <table
              className="mix-amounts-modal__table"
              data-testid="mix-amounts-table"
            >
              <thead>
                <tr>
                  <th>{t('spray.operatorCombo.mixAmounts.product')}</th>
                  <th>{t('spray.operatorCombo.mixAmounts.dosePerTank')}</th>
                </tr>
              </thead>
              <tbody>
                {data.perTankMix.map(p => (
                  <tr key={p.productId}>
                    <td>{p.productName}</td>
                    <td>{formatMeasure(p.dosePerTank, p.doseUnit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mix-amounts-modal__footnote">
              {t('spray.operatorCombo.mixAmounts.footnote')}
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default MixAmountsModal;
